// modules/website/components/TestimonialsComponent.tsx

import React, { useState, useEffect } from 'react';
import { TestimonialsData } from '@/types';
import { ChevronLeft, ChevronRight, Star } from 'lucide-react';

interface Props {
  data: TestimonialsData;
  settings: any;
}

export const TestimonialsComponent: React.FC<Props> = ({ data, settings }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (data.autoplay && data.testimonials.length > 1) {
      const interval = setInterval(() => {
        setCurrentIndex((prev) => (prev + 1) % data.testimonials.length);
      }, data.interval || 5000);
      
      return () => clearInterval(interval);
    }
  }, [data.autoplay, data.interval, data.testimonials.length]);

  const next = () => {
    setCurrentIndex((prev) => (prev + 1) % data.testimonials.length);
  };

  const prev = () => {
    setCurrentIndex((prev) => (prev - 1 + data.testimonials.length) % data.testimonials.length);
  };

  if (data.testimonials.length === 0) {
    return null;
  }

  const currentTestimonial = data.testimonials[currentIndex];

  return (
    <div 
      className="py-20 px-6"
      style={{
        backgroundColor: settings.backgroundColor || '#f9fafb',
      }}
    >
      <div className="max-w-4xl mx-auto">
        {data.title && (
          <h2 className="text-4xl font-bold text-center mb-12">
            {data.title}
          </h2>
        )}
        
        <div className="relative bg-white rounded-2xl shadow-2xl p-12">
          {/* Stars */}
          <div className="flex justify-center mb-6">
            {[...Array(5)].map((_, i) => (
              <Star
                key={i}
                size={24}
                className={i < currentTestimonial.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}
              />
            ))}
          </div>

          {/* Quote */}
          <blockquote className="text-xl text-gray-700 text-center italic mb-8 leading-relaxed">
            "{currentTestimonial.text}"
          </blockquote>

          {/* Author */}
          <div className="flex items-center justify-center gap-4">
            {currentTestimonial.image && (
              <img 
                src={currentTestimonial.image} 
                alt={currentTestimonial.name}
                className="w-16 h-16 rounded-full object-cover"
              />
            )}
            <div className="text-center">
              <div className="font-bold text-lg">{currentTestimonial.name}</div>
              {currentTestimonial.role && (
                <div className="text-gray-500">{currentTestimonial.role}</div>
              )}
            </div>
          </div>

          {/* Navigation */}
          {data.testimonials.length > 1 && (
            <>
              <button
                onClick={prev}
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-white hover:bg-gray-100 p-3 rounded-full shadow-lg transition-all"
              >
                <ChevronLeft size={24} />
              </button>
              <button
                onClick={next}
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-white hover:bg-gray-100 p-3 rounded-full shadow-lg transition-all"
              >
                <ChevronRight size={24} />
              </button>
            </>
          )}

          {/* Indicators */}
          {data.testimonials.length > 1 && (
            <div className="flex justify-center gap-2 mt-8">
              {data.testimonials.map((_: any, index: number) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`w-3 h-3 rounded-full transition-all ${
                    index === currentIndex ? 'bg-blue-600 w-8' : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
